import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { FileText, Upload, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
// Allowed MIME types (matches backend schema)
const ALLOWED_MIME_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/msword',
    'application/vnd.ms-excel',
    'application/vnd.ms-powerpoint',
    'text/plain',
    'text/markdown',
    'text/html',
    'text/csv',
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'application/zip',
    'application/x-zip-compressed',
    // Proof-of-delivery / waiver evidence sourced from email (see baltigo's
    // email extractor and packages/go-common/security/mime_validator.go).
    'message/rfc822',
    'application/vnd.ms-outlook',
    'application/x-msoutlook'
];
const MAX_FILE_SIZE = 250 * 1024 * 1024; // 250MB
// Browsers frequently report an empty (or a generic, unrecognised) file.type
// for .eml/.msg — there's no fixed magic-byte signature for mail the browser
// sniffer recognises the way it does for a PDF or a zip. Gating purely on
// MIME therefore rejects real evidence files. Falling back to the extension
// here is safe: it's a convenience filter only — the authoritative checks are
// server-side, in security.ValidateDocument and again at parse time in
// baltigo's email parser (services/baltigo/pkg/intelligence/emailsource.go),
// so a permissive-by-extension client gate doesn't weaken security.
const EXTENSION_FALLBACK_ALLOWLIST = [
    '.eml',
    '.msg'
];
function getFileExtension(filename) {
    const dotIndex = filename.lastIndexOf('.');
    return dotIndex === -1 ? '' : filename.slice(dotIndex).toLowerCase();
}
/**
 * Validates a file before upload.
 * Returns an error message if invalid, null if valid.
 */ function validateFile(file) {
    if (file.size === 0) {
        return 'Empty file';
    }
    if (file.size > MAX_FILE_SIZE) {
        return 'File exceeds 250MB limit';
    }
    if (ALLOWED_MIME_TYPES.includes(file.type)) {
        return null;
    }
    if (EXTENSION_FALLBACK_ALLOWLIST.includes(getFileExtension(file.name))) {
        return null;
    }
    return `Unsupported file type: ${file.type || 'unknown'}`;
}
/**
 * Format file size for display
 */ function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = [
        'Bytes',
        'KB',
        'MB',
        'GB'
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round(bytes / k ** i * 100) / 100} ${sizes[i]}`;
}
/**
 * Reusable file uploader component with drag-and-drop support.
 * Extracted from ProjectFilesSection for use in CreateProjectDialog.
 */ export function FileUploader({ files, onChange, disabled = false, inputTestid, buttonTestid }) {
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const handleAddFiles = useCallback((newFiles)=>{
        // Validate files and filter out invalid ones
        const validFiles = [];
        const invalidFiles = [];
        for (const file of newFiles){
            const error = validateFile(file);
            if (error) {
                invalidFiles.push({
                    name: file.name,
                    error
                });
            } else {
                // Check for duplicates by name
                const isDuplicate = files.some((f)=>f.name === file.name);
                if (isDuplicate) {
                    invalidFiles.push({
                        name: file.name,
                        error: 'File already added'
                    });
                } else {
                    validFiles.push(file);
                }
            }
        }
        // Show errors for invalid files
        invalidFiles.forEach((f)=>{
            toast.error(`${f.name}: ${f.error}`);
        });
        if (validFiles.length > 0) {
            const updatedFiles = [
                ...files,
                ...validFiles
            ];
            onChange(updatedFiles);
            toast.success(`${validFiles.length} ${validFiles.length === 1 ? 'file' : 'files'} added`);
        }
    }, [
        files,
        onChange
    ]);
    const handleRemoveFile = useCallback((index)=>{
        onChange(files.filter((_, i)=>i !== index));
    }, [
        files,
        onChange
    ]);
    // Drag-and-drop handlers (reused from ProjectFilesSection)
    const handleDragOver = useCallback((e)=>{
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsDragOver(true);
        }
    }, [
        disabled
    ]);
    const handleDragLeave = useCallback((e)=>{
        e.preventDefault();
        e.stopPropagation();
        const relatedTarget = e.relatedTarget;
        const currentTarget = e.currentTarget;
        if (!relatedTarget || !(relatedTarget instanceof Node) || !currentTarget.contains(relatedTarget)) {
            setIsDragOver(false);
        }
    }, []);
    const handleDrop = useCallback((e)=>{
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        if (disabled) return;
        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) {
            handleAddFiles(droppedFiles);
        }
    }, [
        disabled,
        handleAddFiles
    ]);
    const handleFileInputChange = useCallback((e)=>{
        const selectedFiles = e.target.files;
        if (selectedFiles && selectedFiles.length > 0) {
            handleAddFiles(Array.from(selectedFiles));
        }
        // Reset input so the same file can be selected again
        e.target.value = '';
    }, [
        handleAddFiles
    ]);
    const handleClickUpload = (e)=>{
        // Don't preventDefault - it blocks the file picker!
        e.stopPropagation();
        if (!disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                onDragOver: handleDragOver,
                onDragLeave: handleDragLeave,
                onDrop: handleDrop,
                onClick: handleClickUpload,
                className: `
          w-full relative border-2 border-dashed rounded-lg p-6 transition-colors
          ${isDragOver ? 'border-primary bg-accent' : 'border-border bg-muted/40'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary/60'}
        `,
                "data-testid": buttonTestid,
                "aria-label": "Upload files",
                disabled: disabled,
                onKeyDown: (e)=>{
                    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                        e.preventDefault();
                        fileInputRef.current?.click();
                    }
                },
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col items-center gap-2 text-center pointer-events-none",
                        children: [
                            /*#__PURE__*/ _jsx(Upload, {
                                className: `w-8 h-8 ${isDragOver ? 'text-primary' : 'text-muted-foreground'}`
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm font-medium text-foreground",
                                        children: isDragOver ? 'Drop files here' : 'Click or drag files here'
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-muted-foreground mt-1",
                                        children: "PDF, DOCX, XLSX, TXT, ZIP (max 250MB per file)"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        ref: fileInputRef,
                        "data-testid": inputTestid,
                        type: "file",
                        multiple: true,
                        accept: [
                            ...ALLOWED_MIME_TYPES,
                            ...EXTENSION_FALLBACK_ALLOWLIST
                        ].join(','),
                        onChange: handleFileInputChange,
                        className: "hidden",
                        disabled: disabled
                    })
                ]
            }),
            files.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-sm font-medium text-foreground",
                        "data-testid": Testids.project.uploadsReady,
                        children: [
                            files.length,
                            " ",
                            files.length === 1 ? 'file' : 'files',
                            " ready to upload"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "space-y-1 max-h-40 overflow-y-auto",
                        children: files.map((file, index)=>/*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between p-2 bg-card border border-border rounded-md text-sm",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2 flex-1 min-w-0",
                                        children: [
                                            /*#__PURE__*/ _jsx(FileText, {
                                                className: "w-4 h-4 text-muted-foreground flex-shrink-0"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "break-all font-medium text-foreground",
                                                children: file.name
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "text-xs text-muted-foreground flex-shrink-0",
                                                children: [
                                                    "(",
                                                    formatFileSize(file.size),
                                                    ")"
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: (e)=>{
                                            e.stopPropagation();
                                            handleRemoveFile(index);
                                        },
                                        disabled: disabled,
                                        className: "p-1 hover:bg-accent rounded transition-colors disabled:opacity-50",
                                        title: "Remove file",
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "w-4 h-4 text-muted-foreground"
                                        })
                                    })
                                ]
                            }, `${file.name}-${index}`))
                    })
                ]
            })
        ]
    });
}
